import { ref, computed, readonly } from 'vue';
import type { Ref } from 'vue';
import { WEBSOCKET_CONFIG, ENV_CONFIG } from '@/config/constants';
import type { ServerMessage, ClientMessage } from '@/types/generated';
import { getMetrics } from '@/telemetry/metrics';
import { reportClientError, ErrorLocation } from '@/telemetry/errors';
import { startHeartbeat, stopHeartbeat, recordPongRtt } from '@/telemetry/heartbeat';
import { _clearAuthState } from '@/composables/useAuth';
import { checkWebSocketVersion } from '@/composables/useVersionCheck';

const TRACKED_REQUEST_TYPES = new Set([
  'SubmitWord',
  'FindMatch',
  'ReconnectRequest',
  'StateRefreshRequest',
]);

function encodedByteLength(value: string): number {
  return new TextEncoder().encode(value).byteLength;
}

export type ConnectionStatus = 'connecting' | 'connected' | 'disconnected' | 'error';

// Derive ServerMessageMap from the generated ServerMessage union type
export type ServerMessageMap = {
  FindingMatch: Extract<ServerMessage, { FindingMatch: unknown }>['FindingMatch'];
  WaitingForOpponent: Extract<ServerMessage, { WaitingForOpponent: unknown }>['WaitingForOpponent'];
  OpponentFound: Extract<ServerMessage, { OpponentFound: unknown }>['OpponentFound'];
  PlayerStatusUpdate: Extract<ServerMessage, { PlayerStatusUpdate: unknown }>['PlayerStatusUpdate'];
  SubmissionResult: Extract<ServerMessage, { SubmissionResult: unknown }>['SubmissionResult'];
  ReconnectionSuccess: Extract<
    ServerMessage,
    { ReconnectionSuccess: unknown }
  >['ReconnectionSuccess'];
  ReconnectionFailed: Extract<ServerMessage, { ReconnectionFailed: unknown }>['ReconnectionFailed'];
  GameSnapshot: Extract<ServerMessage, { GameSnapshot: unknown }>['GameSnapshot'];
  Error: Extract<ServerMessage, { Error: unknown }>['Error'];
  Pong: Extract<ServerMessage, { Pong: unknown }>['Pong'];
  QueueStatus: Extract<ServerMessage, { QueueStatus: unknown }>['QueueStatus'];
  WaitingPlayerNotFound: Extract<
    ServerMessage,
    { WaitingPlayerNotFound: unknown }
  >['WaitingPlayerNotFound'];
  VersionInfo: Extract<ServerMessage, { VersionInfo: unknown }>['VersionInfo'];
};

type MessageHandler<T = any> = (data: T) => void;

interface WebSocketOptions {
  url?: string;
  reconnectAttempts?: number;
  reconnectDelay?: number;
  maxReconnectDelay?: number;
  backoffFactor?: number;
}

// Message types that should be queued for replay when components mount
// These represent current state rather than time-sensitive events
const QUEUEABLE_MESSAGE_TYPES = new Set(['FindingMatch', 'GameSnapshot']);

interface QueuedMessage {
  type: string;
  data: any;
  timestamp: number;
}

/**
 * WebSocket Singleton Manager
 *
 * Manages a single WebSocket connection shared across all components.
 * Uses reference counting to determine when to maintain/close the connection.
 * Supports multiple message handlers per message type.
 */
class WebSocketManager {
  private static instance: WebSocketManager | null = null;

  // Bound event handlers (stored for proper removal)
  private boundHandleMessage: (event: MessageEvent) => void;
  private boundHandleClose: (event: CloseEvent) => void;
  private boundHandleError: (event: Event) => void;

  // Connection state
  private ws: WebSocket | null = null;
  private connectionStatus = ref<ConnectionStatus>('disconnected');
  private lastError = ref<string | null>(null);
  private connectionPromise: Promise<void> | null = null;

  // Reference counting for component lifecycle management
  private refCount: number = 0;
  private disconnectTimeoutId: number | null = null;

  // Message handling
  private messageHandlers = new Map<string, Set<MessageHandler>>();

  // Telemetry: track connection start for duration metric
  private connectionStartTime: number = 0;

  // Telemetry: request→response RTT correlation
  private inflightRequests = new Map<string, number>();
  // Telemetry: disconnect-to-reconnect duration
  private disconnectTime: number = 0;
  // Telemetry: WebSocket open to first server message
  private firstMessageAfterOpenReceived = false;
  // Telemetry: time-to-playable (FindMatch/RequestBotGame send → first OpponentFound/GameSnapshot)
  private findMatchSendTime: number = 0;

  // Message queue for state messages that arrive before handlers are registered
  private messageQueue = new Map<string, QueuedMessage>();
  private readonly MAX_QUEUE_AGE_MS = 30000; // 30 seconds

  // Connection configuration
  private options: WebSocketOptions = {
    url: ENV_CONFIG.getWebSocketUrl(),
    reconnectAttempts: WEBSOCKET_CONFIG.RECONNECT_ATTEMPTS,
    reconnectDelay: WEBSOCKET_CONFIG.RECONNECT_DELAY,
    maxReconnectDelay: WEBSOCKET_CONFIG.MAX_RECONNECT_DELAY,
    backoffFactor: WEBSOCKET_CONFIG.BACKOFF_FACTOR,
  };

  // Reconnection state
  private reconnectCount = 0;
  private reconnectTimeoutId: number | null = null;
  private currentReconnectAttempt = ref(0);
  private totalReconnectAttempts = ref<number>(WEBSOCKET_CONFIG.RECONNECT_ATTEMPTS);
  private canManualReconnect = ref(false);

  /**
   * Get singleton instance
   */
  static getInstance(): WebSocketManager {
    if (!this.instance) {
      this.instance = new WebSocketManager();
    }
    return this.instance;
  }

  private constructor() {
    // Bind handlers once for proper event listener removal
    this.boundHandleMessage = this.handleMessage.bind(this);
    this.boundHandleClose = this.handleClose.bind(this);
    this.boundHandleError = this.handleError.bind(this);
  }

  /**
   * Acquire a reference to the WebSocket connection
   * Called when a component starts using the WebSocket
   */
  acquire(): void {
    this.refCount++;
    console.log(`[WebSocketManager] Acquired reference, refCount: ${this.refCount}`);

    // Cancel any pending disconnect
    this.clearDisconnectTimeout();
  }

  /**
   * Release a reference to the WebSocket connection
   * Called when a component stops using the WebSocket
   */
  release(): void {
    this.refCount--;
    console.log(`[WebSocketManager] Released reference, refCount: ${this.refCount}`);

    if (this.refCount <= 0) {
      // Delay disconnect to handle rapid component transitions
      this.scheduleDisconnect();
    }
  }

  /**
   * Configure WebSocket options
   */
  configure(options: Partial<WebSocketOptions>): void {
    this.options = { ...this.options, ...options };

    // Update reactive refs when configuration changes
    if (options.reconnectAttempts !== undefined) {
      this.totalReconnectAttempts.value = options.reconnectAttempts;
    }
  }

  /**
   * Connect to WebSocket server
   */
  async connect(): Promise<void> {
    // Return existing connection promise if already connecting
    if (this.connectionPromise) {
      return this.connectionPromise;
    }

    // Return immediately if already connected
    if (this.ws?.readyState === WebSocket.OPEN) {
      return Promise.resolve();
    }

    // Create new connection promise
    this.connectionPromise = this.establishConnection();

    try {
      await this.connectionPromise;
    } finally {
      // Clear the connection promise once resolved/rejected
      this.connectionPromise = null;
    }
  }

  /**
   * Establish the actual WebSocket connection
   */
  private async establishConnection(): Promise<void> {
    // Close existing connection if any
    if (this.ws) {
      this.disconnectInternal();
    }

    this.connectionStatus.value = 'connecting';
    this.lastError.value = null;
    this.clearReconnectTimeout();

    try {
      const socket = new WebSocket(this.options.url!);
      this.ws = socket;
      this.setupEventListeners(socket);

      // Return promise that resolves when connected or rejects on error
      return new Promise((resolve, reject) => {
        const onOpen = () => {
          socket.removeEventListener('open', onOpen);
          socket.removeEventListener('error', onError);
          this.connectionStatus.value = 'connected';
          this.reconnectCount = 0;
          this.lastError.value = null;

          // Reset reactive reconnection state on successful connection
          this.currentReconnectAttempt.value = 0;
          this.canManualReconnect.value = false;

          this.connectionStartTime = Date.now();
          this.firstMessageAfterOpenReceived = false;

          if (this.disconnectTime > 0) {
            getMetrics().wsReconnectDurationMs.record(Date.now() - this.disconnectTime, {
              outcome: 'success',
            });
            this.disconnectTime = 0;
          }

          startHeartbeat(msg => this.sendMessage(msg));

          console.log('[WebSocketManager] Connected successfully');
          resolve();
        };

        const onError = (_event: Event) => {
          socket.removeEventListener('open', onOpen);
          socket.removeEventListener('error', onError);
          this.connectionStatus.value = 'error';
          this.lastError.value = 'Failed to connect to WebSocket';
          getMetrics().wsConnectionFailures.add(1, { reason: 'connect_error' });
          reportClientError(
            new Error('Failed to connect to WebSocket'),
            ErrorLocation.WsConnectError
          );
          reject(new Error('Failed to connect to WebSocket'));
        };

        socket.addEventListener('open', onOpen);
        socket.addEventListener('error', onError);
      });
    } catch (error) {
      this.connectionStatus.value = 'error';
      this.lastError.value = error instanceof Error ? error.message : 'Unknown connection error';
      throw error;
    }
  }

  /**
   * Disconnect from WebSocket
   */
  disconnect(): void {
    this.clearDisconnectTimeout();
    this.clearReconnectTimeout();
    this.reconnectCount = 0;

    // Reset reactive state
    this.currentReconnectAttempt.value = 0;
    this.canManualReconnect.value = false;

    this.disconnectInternal();
  }

  /**
   * Internal disconnect implementation
   */
  private disconnectInternal(): void {
    stopHeartbeat();
    if (this.ws) {
      this.removeEventListeners(this.ws);
      this.ws.close(1000, 'Manual disconnect');
      this.ws = null;
    }

    this.connectionStatus.value = 'disconnected';
    console.log('[WebSocketManager] Disconnected');
  }

  /**
   * Send message through WebSocket
   */
  sendMessage(message: ClientMessage): boolean {
    // Validate input
    if (!message || typeof message !== 'object') {
      console.warn('[WebSocketManager] Invalid message object');
      return false;
    }

    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      console.warn('[WebSocketManager] Cannot send message: WebSocket not connected');
      return false;
    }

    try {
      const messageStr = JSON.stringify(message);
      if (!messageStr || messageStr === '{}') {
        console.warn('[WebSocketManager] Empty or invalid message to send');
        return false;
      }

      this.ws.send(messageStr);
      const type = Object.keys(message)[0] ?? 'unknown';
      const metrics = getMetrics();
      metrics.wsMessagesSent.add(1, { type });
      metrics.wsMessageBytesSent.record(encodedByteLength(messageStr), { type });

      const now = Date.now();
      if (TRACKED_REQUEST_TYPES.has(type)) {
        this.inflightRequests.set(type, now);
      }
      if (type === 'FindMatch' || type === 'RequestBotGame') {
        this.findMatchSendTime = now;
      }

      console.log('[WebSocketManager] Message sent:', message);
      return true;
    } catch (error) {
      reportClientError(error, ErrorLocation.WsSendFailed);
      this.lastError.value = 'Failed to send message';
      return false;
    }
  }

  /**
   * Register message handler
   */
  onMessage<T extends keyof ServerMessageMap>(
    messageType: T,
    handler: (data: ServerMessageMap[T]) => void
  ): () => void {
    if (!this.messageHandlers.has(messageType)) {
      this.messageHandlers.set(messageType, new Set());
    }

    const handlers = this.messageHandlers.get(messageType)!;
    handlers.add(handler);

    console.log(
      `[WebSocketManager] Registered handler for ${messageType}, total handlers: ${handlers.size}`
    );

    // Replay any queued message for this type
    this.replayQueuedMessage(messageType, handler);

    // Return unsubscribe function
    return () => {
      handlers.delete(handler);
      if (handlers.size === 0) {
        this.messageHandlers.delete(messageType);
      }
      console.log(
        `[WebSocketManager] Unregistered handler for ${messageType}, remaining handlers: ${handlers.size}`
      );
    };
  }

  /**
   * Get reactive connection status
   */
  get isConnected(): Readonly<Ref<boolean>> {
    return readonly(computed(() => this.connectionStatus.value === 'connected'));
  }

  /**
   * Get reactive connection status
   */
  get status(): Readonly<Ref<ConnectionStatus>> {
    return readonly(this.connectionStatus);
  }

  /**
   * Get reactive error state
   */
  get error(): Readonly<Ref<string | null>> {
    return readonly(this.lastError);
  }

  /**
   * Get raw WebSocket instance (readonly)
   */
  get websocket(): Readonly<Ref<WebSocket | null>> {
    return readonly(ref(this.ws));
  }

  /**
   * Get current reconnection attempt number
   */
  get reconnectAttempt(): Readonly<Ref<number>> {
    return readonly(this.currentReconnectAttempt);
  }

  /**
   * Get total reconnection attempts configured
   */
  get maxReconnectAttempts(): Readonly<Ref<number>> {
    return readonly(this.totalReconnectAttempts);
  }

  /**
   * Get whether manual reconnect is available
   */
  get manualReconnectAvailable(): Readonly<Ref<boolean>> {
    return readonly(this.canManualReconnect);
  }

  /**
   * Manually trigger reconnection attempt
   * Resets attempt counter and forces immediate reconnection
   */
  async manualReconnect(): Promise<void> {
    console.log('[WebSocketManager] Manual reconnect triggered');

    // Reset reconnection state
    this.reconnectCount = 0;
    this.currentReconnectAttempt.value = 0;
    this.canManualReconnect.value = false;
    this.clearReconnectTimeout();

    // Attempt immediate reconnection
    try {
      await this.connect();
      console.log('[WebSocketManager] Manual reconnect successful');
    } catch (error) {
      reportClientError(error, ErrorLocation.WsManualReconnect);
      this.canManualReconnect.value = true;
    }
  }

  /**
   * Setup WebSocket event listeners
   */
  private setupEventListeners(socket: WebSocket): void {
    socket.addEventListener('message', this.boundHandleMessage);
    socket.addEventListener('close', this.boundHandleClose);
    socket.addEventListener('error', this.boundHandleError);
  }

  /**
   * Remove WebSocket event listeners
   */
  private removeEventListeners(socket: WebSocket): void {
    socket.removeEventListener('message', this.boundHandleMessage);
    socket.removeEventListener('close', this.boundHandleClose);
    socket.removeEventListener('error', this.boundHandleError);
  }

  /**
   * Handle incoming WebSocket messages
   */
  private handleMessage(event: MessageEvent): void {
    // Validate event and data
    if (!event || !event.data) {
      console.warn('[WebSocketManager] Received empty WebSocket message');
      return;
    }

    if (typeof event.data !== 'string') {
      console.warn('[WebSocketManager] Received non-string WebSocket message');
      return;
    }

    try {
      const message = JSON.parse(event.data) as ServerMessage;

      // Validate parsed message
      if (!message || typeof message !== 'object') {
        console.warn('[WebSocketManager] Received invalid message format');
        return;
      }

      // Get message type (first key of the message object)
      const messageKeys = Object.keys(message);
      if (messageKeys.length === 0) {
        console.warn('[WebSocketManager] Received empty message object');
        return;
      }

      const messageType = messageKeys[0] as keyof ServerMessage;
      const messageData = (message as Record<string, unknown>)[messageType as string];

      if (messageType === 'AuthenticationExpired') {
        console.warn(
          '[WebSocketManager] Server reports authentication expired, redirecting to login'
        );
        _clearAuthState();
        window.location.href = '/login';
        return;
      }

      if (messageType === 'VersionInfo') {
        const data = messageData as { version: string };
        checkWebSocketVersion(data.version);
        return;
      }

      const metrics = getMetrics();
      metrics.wsMessagesReceived.add(1, { type: messageType });
      metrics.wsMessageBytesReceived.record(encodedByteLength(event.data), { type: messageType });

      if (!this.firstMessageAfterOpenReceived && this.connectionStartTime > 0) {
        metrics.wsOpenToFirstMessageMs.record(Date.now() - this.connectionStartTime);
        this.firstMessageAfterOpenReceived = true;
      }

      this.recordRequestResponseLag(messageType as string);
      this.recordTimeToPlayable(messageType as string);
      if (messageType === 'Pong') {
        const pongPayload = messageData as { sequence_number: number };
        recordPongRtt(pongPayload.sequence_number);
      }

      console.log(`[WebSocketManager] Received ${messageType}:`, messageData);

      const handlers = this.messageHandlers.get(messageType);
      if (handlers && handlers.size > 0) {
        handlers.forEach(handler => {
          try {
            if (typeof handler === 'function') {
              handler(messageData);
            }
          } catch (error) {
            reportClientError(error, ErrorLocation.WsMessageHandler);
          }
        });
      } else if (QUEUEABLE_MESSAGE_TYPES.has(messageType)) {
        this.queueMessage(messageType, messageData);
        console.log(
          `[WebSocketManager] Queued ${messageType} message (no handlers registered yet)`
        );
      } else {
        console.warn(`[WebSocketManager] No handler for ${messageType}, message dropped`);
      }
    } catch (error) {
      getMetrics().wsMessageParseErrors.add(1);
      reportClientError(error, ErrorLocation.WsMessageParse);
      this.connectionStatus.value = 'error';
      this.lastError.value = 'Failed to parse server message';
      this.canManualReconnect.value = true;
    }
  }

  /**
   * Handle WebSocket connection close
   */
  private handleClose(event: CloseEvent): void {
    this.connectionStatus.value = 'disconnected';
    this.firstMessageAfterOpenReceived = false;
    stopHeartbeat();

    // Record connection duration if we had a valid connection
    if (this.connectionStartTime > 0) {
      getMetrics().wsConnectionDurationMs.record(Date.now() - this.connectionStartTime);
      this.connectionStartTime = 0;
    }

    console.log('[WebSocketManager] Connection closed:', event);

    // RFC 6455 close codes 1001 ("Going Away"), 1012 ("Service Restart"),
    // and 1013 ("Try Again Later") are server-initiated shutdown signals.
    // They arrive with `wasClean=true` because the server completed the
    // close handshake — but we still need to reconnect to the surviving
    // pod (or new replica) so the player's game continues. See #207.
    const isServerShutdown = event.code === 1001 || event.code === 1012 || event.code === 1013;
    const willAttemptReconnect =
      (!event.wasClean || isServerShutdown) &&
      this.reconnectCount < this.options.reconnectAttempts! &&
      this.refCount > 0;

    if (willAttemptReconnect) {
      if (this.disconnectTime === 0) {
        this.disconnectTime = Date.now();
      }
      this.scheduleReconnect(isServerShutdown);
    } else if (this.reconnectCount >= this.options.reconnectAttempts!) {
      this.lastError.value = 'Failed to reconnect after maximum attempts';
      this.connectionStatus.value = 'error';
      // Enable manual reconnect when automatic attempts are exhausted
      this.canManualReconnect.value = true;
      getMetrics().wsReconnectionExhausted.add(1);
      if (this.disconnectTime > 0) {
        getMetrics().wsReconnectDurationMs.record(Date.now() - this.disconnectTime, {
          outcome: 'exhausted',
        });
        this.disconnectTime = 0;
      }
    }
  }

  /**
   * Handle WebSocket errors
   */
  private handleError(_event: Event): void {
    getMetrics().wsConnectionFailures.add(1, { reason: 'protocol_error' });
    reportClientError(new Error('WebSocket protocol error'), ErrorLocation.WsProtocolError);
    this.connectionStatus.value = 'error';
    this.lastError.value = 'WebSocket connection error';
  }

  /**
   * Schedule reconnection attempt.
   *
   * - Server-initiated shutdown (close code 1001/1012/1013): retry near-
   *   immediately (50–200 ms) so a rolling deploy is invisible to the user.
   *   The pod that closed has already been pulled from the EndpointSlice by
   *   the preStop hook, so the next connection lands on a healthy pod.
   * - Other unclean closes: exponential backoff with ±30 % jitter to avoid
   *   thundering-herd retries during an incident. Default progression:
   *   1s, 2s, 4s, 8s, 16s, 30s (capped at maxDelay), each multiplied by a
   *   random factor in [0.7, 1.3].
   */
  private scheduleReconnect(serverShutdown: boolean = false): void {
    this.reconnectCount++;
    getMetrics().wsReconnections.add(1);

    // Update reactive state
    this.currentReconnectAttempt.value = this.reconnectCount;
    this.totalReconnectAttempts.value = this.options.reconnectAttempts!;
    this.canManualReconnect.value = false;

    let delay: number;
    if (serverShutdown) {
      // Small random jitter; no exponential growth on first attempt.
      delay = 50 + Math.random() * 150;
    } else {
      const baseDelay = this.options.reconnectDelay!;
      const backoffFactor = this.options.backoffFactor!;
      const maxDelay = this.options.maxReconnectDelay!;
      const exponentialDelay = baseDelay * Math.pow(backoffFactor, this.reconnectCount - 1);
      const jitter = 0.7 + Math.random() * 0.6; // ±30%
      delay = Math.min(exponentialDelay * jitter, maxDelay);
    }

    console.log(
      `[WebSocketManager] Scheduling reconnection in ${Math.round(delay)}ms ` +
        `(attempt ${this.reconnectCount}/${this.options.reconnectAttempts}) ` +
        `${serverShutdown ? '[server shutdown — fast retry]' : '[exponential backoff + jitter]'}`
    );

    this.reconnectTimeoutId = window.setTimeout(() => {
      if (this.refCount > 0) {
        this.connect().catch(error => {
          reportClientError(error, ErrorLocation.WsReconnectFailed);
        });
      }
    }, delay);
  }

  /**
   * Schedule disconnect with delay
   */
  private scheduleDisconnect(): void {
    // Delay disconnect to handle rapid component transitions
    this.disconnectTimeoutId = window.setTimeout(() => {
      if (this.refCount <= 0) {
        console.log('[WebSocketManager] No active references, disconnecting');
        this.disconnect();
      }
    }, WEBSOCKET_CONFIG.DISCONNECT_DELAY); // Disconnect delay
  }

  /**
   * Clear disconnect timeout
   */
  private clearDisconnectTimeout(): void {
    if (this.disconnectTimeoutId !== null) {
      clearTimeout(this.disconnectTimeoutId);
      this.disconnectTimeoutId = null;
    }
  }

  /**
   * Clear reconnect timeout
   */
  private clearReconnectTimeout(): void {
    if (this.reconnectTimeoutId !== null) {
      clearTimeout(this.reconnectTimeoutId);
      this.reconnectTimeoutId = null;
    }
  }

  private queueMessage(messageType: string, messageData: any): void {
    if (this.messageQueue.has(messageType)) {
      console.log(`[WebSocketManager] Replacing queued ${messageType} with newer message`);
    }

    this.messageQueue.set(messageType, {
      type: messageType,
      data: messageData,
      timestamp: Date.now(),
    });

    this.cleanupOldQueuedMessages();
  }

  private replayQueuedMessage<T extends keyof ServerMessageMap>(
    messageType: T,
    handler: (data: ServerMessageMap[T]) => void
  ): void {
    const queuedMessage = this.messageQueue.get(messageType);
    if (queuedMessage) {
      const age = Date.now() - queuedMessage.timestamp;
      if (age <= this.MAX_QUEUE_AGE_MS) {
        console.log(`[WebSocketManager] Replaying queued ${messageType} message (age: ${age}ms)`);
        try {
          handler(queuedMessage.data);
          this.messageQueue.delete(messageType);
        } catch (error) {
          reportClientError(error, ErrorLocation.WsQueuedMessageReplay);
        }
      } else {
        console.log(
          `[WebSocketManager] Discarding stale queued ${messageType} message (age: ${age}ms)`
        );
        this.messageQueue.delete(messageType);
      }
    }
  }

  /**
   * Clean up messages that are too old
   */
  private cleanupOldQueuedMessages(): void {
    const now = Date.now();
    for (const [messageType, queuedMessage] of this.messageQueue.entries()) {
      if (now - queuedMessage.timestamp > this.MAX_QUEUE_AGE_MS) {
        console.log(`[WebSocketManager] Cleaning up stale queued ${messageType} message`);
        this.messageQueue.delete(messageType);
      }
    }
  }

  private recordRequestResponseLag(messageType: string): void {
    let requestType: string | null = null;
    switch (messageType) {
      case 'SubmissionResult':
        requestType = 'SubmitWord';
        break;
      case 'OpponentFound':
        requestType = 'FindMatch';
        break;
      case 'ReconnectionSuccess':
      case 'ReconnectionFailed':
        requestType = 'ReconnectRequest';
        break;
      case 'GameSnapshot':
        // GameSnapshot also arrives unsolicited at game start; only correlate
        // when a StateRefreshRequest is actually in flight.
        if (this.inflightRequests.has('StateRefreshRequest')) {
          requestType = 'StateRefreshRequest';
        }
        break;
    }
    if (requestType === null) return;
    const sendTime = this.inflightRequests.get(requestType);
    if (typeof sendTime !== 'number') return;
    this.inflightRequests.delete(requestType);
    getMetrics().wsRequestRttMs.record(Date.now() - sendTime, { request_type: requestType });
  }

  private recordTimeToPlayable(messageType: string): void {
    if (messageType !== 'OpponentFound' && messageType !== 'GameSnapshot') return;
    if (this.findMatchSendTime <= 0) return;
    getMetrics().gameTimeToPlayableMs.record(Date.now() - this.findMatchSendTime, {
      end_event: messageType,
    });
    this.findMatchSendTime = 0;
  }
}

export { WebSocketManager };
